import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import offMarketPropertiesService from '../../api/services/offMarketProperties';
import type { OffMarketMatch } from '../../api/services/offMarketProperties';

interface BuyerOffMarketMatchesState {
  matches: OffMarketMatch[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  unviewedCount: number;
}

const initialState: BuyerOffMarketMatchesState = {
  matches: [],
  loading: false,
  error: null,
  totalCount: 0,
  unviewedCount: 0,
};

// Thunks
export const fetchBuyerOffMarketMatches = createAsyncThunk(
  'buyerOffMarketMatches/fetchMatches',
  async () => {
    const matches = await offMarketPropertiesService.getMatches();
    return matches;
  }
);

export const updateOffMarketMatchStatus = createAsyncThunk(
  'buyerOffMarketMatches/updateStatus',
  async ({
    id,
    updates,
  }: {
    id: string;
    updates: {
      isViewed?: boolean;
      isContacted?: boolean;
      isDismissed?: boolean;
    };
  }) => {
    const updatedMatch = await offMarketPropertiesService.updateMatch(
      id,
      updates
    );
    return updatedMatch;
  }
);

export const dismissOffMarketMatch = createAsyncThunk(
  'buyerOffMarketMatches/dismiss',
  async (id: string) => {
    const updatedMatch = await offMarketPropertiesService.dismissMatch(id);
    return updatedMatch;
  }
);

export const markOffMarketMatchViewed = createAsyncThunk(
  'buyerOffMarketMatches/markViewed',
  async (id: string) => {
    const updatedMatch = await offMarketPropertiesService.markViewed(id);
    return updatedMatch;
  }
);

export const markOffMarketMatchContacted = createAsyncThunk(
  'buyerOffMarketMatches/markContacted',
  async (id: string) => {
    const updatedMatch = await offMarketPropertiesService.markContacted(id);
    return updatedMatch;
  }
);

// Slice
const buyerOffMarketMatchesSlice = createSlice({
  name: 'buyerOffMarketMatches',
  initialState,
  reducers: {
    clearOffMarketMatches: (state) => {
      state.matches = [];
      state.totalCount = 0;
      state.unviewedCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBuyerOffMarketMatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuyerOffMarketMatches.fulfilled, (state, action) => {
        state.loading = false;
        state.matches = action.payload;
        state.totalCount = action.payload.length;
        state.unviewedCount = action.payload.filter((m) => !m.isViewed).length;
      })
      .addCase(fetchBuyerOffMarketMatches.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || 'Failed to fetch off-market matches';
      })
      .addCase(updateOffMarketMatchStatus.fulfilled, (state, action) => {
        const index = state.matches.findIndex(
          (m) => m.id === action.payload.id
        );
        if (index !== -1) {
          state.matches[index] = action.payload;
          state.unviewedCount = state.matches.filter((m) => !m.isViewed).length;
        }
      })
      .addCase(dismissOffMarketMatch.fulfilled, (state, action) => {
        state.matches = state.matches.filter((m) => m.id !== action.payload.id);
        state.totalCount = state.matches.length;
        state.unviewedCount = state.matches.filter((m) => !m.isViewed).length;
      })
      .addCase(markOffMarketMatchViewed.fulfilled, (state, action) => {
        const index = state.matches.findIndex(
          (m) => m.id === action.payload.id
        );
        if (index !== -1) {
          state.matches[index] = action.payload;
          state.unviewedCount = state.matches.filter((m) => !m.isViewed).length;
        }
      })
      .addCase(markOffMarketMatchContacted.fulfilled, (state, action) => {
        const index = state.matches.findIndex(
          (m) => m.id === action.payload.id
        );
        if (index !== -1) {
          state.matches[index] = action.payload;
        }
      });
  },
});

export const { clearOffMarketMatches } = buyerOffMarketMatchesSlice.actions;
export default buyerOffMarketMatchesSlice.reducer;
